import { useCallback, useEffect, useState } from 'react';
import { Loader2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Can } from '@/auth/guards';
import { latestDesk, queueDesk, type RunRequest } from '@/api/runRequests';
import { SessionButton } from './SessionButton';
import { POLL_MS, isOpen } from './runState';

/**
 * "Start desk session" on Today's desk item (plan W13): queues the attended
 * desk session; the Mac's dispatcher opens it as a chat in claude-manager
 * (SessionButton shows it). Starts from the request the item carries.
 */
export function DeskButton({ initial }: { initial: RunRequest | null }) {
  const [req, setReq] = useState<RunRequest | null>(initial);
  const [busy, setBusy] = useState(false);
  const open = isOpen(req);

  const load = useCallback(async () => {
    try { setReq((await latestDesk()).desk_session); } catch { /* the button still works */ }
  }, []);
  useEffect(() => {
    if (!open || req?.claimedAt) return;
    const t = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(t);
  }, [open, req?.claimedAt, load]);

  const start = async () => {
    setBusy(true);
    try {
      const r = await queueDesk();
      setReq(r.request);
      if (r.joined) toast.message('The desk session is already open');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not start it');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Can code="orders:write:nudges">
      <SessionButton request={req}>
        <Button size="sm" className="h-8 gap-1.5 px-3 text-xs" disabled={busy} onClick={() => void start()}>
          {busy ? <Loader2 className="animate-spin" /> : <Play />}Start desk session
        </Button>
      </SessionButton>
    </Can>
  );
}
